import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';

const SessionList = () => {
  const [sessions, setSessions] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    quiz_id: '',
    session_name: '',
    scheduled_start: '',
    scheduled_end: '',
    instructions: ''
  });
  const [showInstructions, setShowInstructions] = useState(false);

  // Default instructions template
  const defaultInstructions = `- Once started, the timer cannot be paused
- Make sure you have a stable internet connection
- Do not refresh or close the browser during the exam
- The assessment must be completed in one sitting; you cannot save and resume later
- You may review and change your answers before final submission
- There is no negative marking for incorrect answers`;

  useEffect(() => {
    fetchData();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, quizzesRes] = await Promise.all([
        api.get('/sessions'),
        api.get('/quizzes')
      ]);
      setSessions(sessionsRes.data);
      setQuizzes(quizzesRes.data);
    } catch (error) {
      setError('Failed to load sessions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Convert datetime-local string to ISO string preserving local time
  const toISOWithTimezone = (datetimeLocalStr) => {
    if (!datetimeLocalStr) return null;
    const date = new Date(datetimeLocalStr);
    return date.toISOString();
  };

  // Convert ISO/UTC date string to datetime-local format
  const toDateTimeLocal = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    setError('');

    const payload = {
      quiz_id: formData.quiz_id,
      session_name: formData.session_name,
      scheduled_start: toISOWithTimezone(formData.scheduled_start),
      scheduled_end: toISOWithTimezone(formData.scheduled_end),
      instructions: formData.instructions || null
    };

    try {
      await api.post('/sessions', payload);
      closeModal();
      fetchData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create session');
    }
  };

  const handleUpdateSession = async (e) => {
    e.preventDefault();
    setError('');

    const payload = {
      session_name: formData.session_name,
      scheduled_start: toISOWithTimezone(formData.scheduled_start),
      scheduled_end: toISOWithTimezone(formData.scheduled_end),
      instructions: formData.instructions || null
    };

    try {
      await api.put(`/sessions/${editingSession.id}`, payload);
      closeModal();
      fetchData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update session');
    }
  };

  const openEditModal = (session) => {
    setEditingSession(session);
    setFormData({
      quiz_id: session.quiz_id,
      session_name: session.session_name,
      scheduled_start: toDateTimeLocal(session.scheduled_start),
      scheduled_end: toDateTimeLocal(session.scheduled_end),
      instructions: session.instructions || ''
    });
    setShowInstructions(!!session.instructions);
    setShowModal(true);
    setOpenMenu(null);
  };

  const openCreateModal = () => {
    setEditingSession(null);
    setFormData({ quiz_id: '', session_name: '', scheduled_start: '', scheduled_end: '', instructions: '' });
    setShowInstructions(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSession(null);
    setFormData({ quiz_id: '', session_name: '', scheduled_start: '', scheduled_end: '', instructions: '' });
    setShowInstructions(false);
  };

  const handleDeleteSession = async () => {
    if (!deleteModal) return;
    try {
      await api.delete(`/sessions/${deleteModal.id}`);
      setDeleteModal(null);
      fetchData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete session');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getSessionStatus = (session) => {
    const now = new Date();
    const start = new Date(session.scheduled_start);
    const end = new Date(session.scheduled_end);

    if (!session.is_active) return { label: 'Inactive', class: 'badge-danger', color: '#ef4444' };
    if (now < start) return { label: 'Scheduled', class: 'badge-info', color: '#3b82f6' };
    if (now > end) return { label: 'Ended', class: 'badge-secondary', color: '#6b7280' };
    return { label: 'Active', class: 'badge-success', color: '#10b981' };
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <div>
      <div className="flex flex-between flex-center mb-5">
        <div>
          <h1>Exam Sessions</h1>
          <p className="text-muted">Schedule exams and manage candidate access codes</p>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Session
        </button>
      </div>

      {error && <div className="alert alert-error mb-4">{error}</div>}

      {sessions.length === 0 ? (
        <div className="card empty-state">
          <h3>No exam sessions yet</h3>
          <p>Create a session to schedule exams and generate individual access codes for candidates</p>
          <button className="btn btn-primary mt-3" onClick={openCreateModal}>
            Create Session
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {sessions.map(session => {
            const status = getSessionStatus(session);
            return (
              <div
                key={session.id}
                className="card"
                style={{
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  transition: 'box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                onClick={() => navigate(`/sessions/${session.id}`)}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
              >
                {/* Status indicator */}
                <div style={{
                  width: '8px',
                  height: '60px',
                  borderRadius: '4px',
                  background: status.color,
                  flexShrink: 0
                }} />

                {/* Main content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="flex flex-center gap-3" style={{ marginBottom: '6px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{session.session_name}</h3>
                    <span className={`badge ${status.class}`} style={{ fontSize: '11px' }}>
                      {status.label}
                    </span>
                  </div>
                  <p className="text-muted" style={{ margin: '0 0 8px 0', fontSize: '13px' }}>
                    {session.quiz_title}
                  </p>
                  <div className="flex gap-4" style={{ fontSize: '13px', color: '#6b7280' }}>
                    <span>{session.invite_count || 0} candidates</span>
                    <span>{session.completed_count || 0} completed</span>
                  </div>
                </div>

                {/* Schedule info */}
                <div style={{
                  padding: '8px 12px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '13px',
                  color: '#374151',
                  minWidth: '160px'
                }}>
                  <div style={{ marginBottom: '4px' }}>
                    <span style={{ color: '#6b7280' }}>Start:</span> {formatDate(session.scheduled_start)}
                  </div>
                  <div>
                    <span style={{ color: '#6b7280' }}>End:</span> {formatDate(session.scheduled_end)}
                  </div>
                </div>

                {/* Actions dropdown */}
                <div style={{ position: 'relative' }} ref={openMenu === session.id ? menuRef : null} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => setOpenMenu(openMenu === session.id ? null : session.id)}
                    style={{
                      background: 'none',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    ...
                  </button>

                  {openMenu === session.id && (
                    <div style={{
                      position: 'absolute',
                      right: 0,
                      top: '100%',
                      marginTop: '4px',
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      minWidth: '150px',
                      zIndex: 100,
                      overflow: 'hidden'
                    }}>
                      <button
                        onClick={() => navigate(`/sessions/${session.id}`)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '10px 16px',
                          background: 'white',
                          border: 'none',
                          textAlign: 'left',
                          color: '#374151',
                          cursor: 'pointer',
                          fontSize: '14px',
                          borderBottom: '1px solid #f3f4f6'
                        }}
                        onMouseEnter={e => e.target.style.background = '#f9fafb'}
                        onMouseLeave={e => e.target.style.background = 'white'}
                      >
                        Manage Candidates
                      </button>
                      <button
                        onClick={() => openEditModal(session)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '10px 16px',
                          background: 'white',
                          border: 'none',
                          textAlign: 'left',
                          color: '#374151',
                          cursor: 'pointer',
                          fontSize: '14px',
                          borderBottom: '1px solid #f3f4f6'
                        }}
                        onMouseEnter={e => e.target.style.background = '#f9fafb'}
                        onMouseLeave={e => e.target.style.background = 'white'}
                      >
                        Edit Session
                      </button>
                      <button
                        onClick={() => { setDeleteModal(session); setOpenMenu(null); }}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '10px 16px',
                          background: 'white',
                          border: 'none',
                          textAlign: 'left',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                        onMouseEnter={e => e.target.style.background = '#fef2f2'}
                        onMouseLeave={e => e.target.style.background = 'white'}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" {...overlayDismiss(() => setDeleteModal(null))}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Session</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete "<strong>{deleteModal.session_name}</strong>"?</p>
              <p className="text-muted mt-2">This will remove all candidate invites and attempt records. This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteSession}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Session Modal */}
      {showModal && (
        <div className="modal-overlay" {...overlayDismiss(() => closeModal())}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>{editingSession ? 'Edit Session' : 'Create Exam Session'}</h3>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="modal-body">
              <form onSubmit={editingSession ? handleUpdateSession : handleCreateSession}>
                {!editingSession && (
                  <div className="form-group">
                    <label>Select Quiz *</label>
                    <select
                      value={formData.quiz_id}
                      onChange={e => setFormData({ ...formData, quiz_id: e.target.value })}
                      required
                    >
                      <option value="">-- Select a quiz --</option>
                      {quizzes.map(quiz => (
                        <option key={quiz.id} value={quiz.id}>
                          {quiz.title} ({quiz.question_count || 0} questions)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Session Name *</label>
                  <input
                    type="text"
                    value={formData.session_name}
                    onChange={e => setFormData({ ...formData, session_name: e.target.value })}
                    placeholder="e.g., Team A - December 2024"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Start Date/Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_start}
                    onChange={e => setFormData({ ...formData, scheduled_start: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Date/Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_end}
                    onChange={e => setFormData({ ...formData, scheduled_end: e.target.value })}
                    required
                  />
                </div>

                {/* Instructions Section */}
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: showInstructions ? '12px' : '0'
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}>
                      <input
                        type="checkbox"
                        checked={showInstructions}
                        onChange={e => {
                          setShowInstructions(e.target.checked);
                          if (e.target.checked && !formData.instructions) {
                            setFormData({ ...formData, instructions: defaultInstructions });
                          }
                        }}
                        style={{ width: '16px', height: '16px' }}
                      />
                      Custom Exam Instructions
                    </label>
                    {showInstructions && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, instructions: defaultInstructions })}
                        style={{
                          background: '#e5e7eb',
                          border: 'none',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Reset to Default
                      </button>
                    )}
                  </div>
                  {showInstructions && (
                    <>
                      <textarea
                        value={formData.instructions}
                        onChange={e => setFormData({ ...formData, instructions: e.target.value })}
                        placeholder="Enter exam instructions (one per line, use - for bullet points)"
                        style={{
                          width: '100%',
                          minHeight: '150px',
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          boxSizing: 'border-box'
                        }}
                      />
                      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
                        Each line starting with "-" will be shown as a bullet point to candidates before they start the exam.
                      </p>
                    </>
                  )}
                </div>

                <div className="flex gap-3 mt-4">
                  <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                    {editingSession ? 'Update Session' : 'Create Session'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionList;
